import React, { FC, useEffect, useMemo, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LockIcon from '@material-ui/icons/Lock';
import SearchIcon from '@material-ui/icons/Search';
import StoreIcon from '@material-ui/icons/Store';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import clsx from 'clsx';
import { switchChildAccount } from 'clients/ChainClient';
import { isValid } from 'clients/Clients';
import { getSessionTokenClient } from 'utils/SessionUtils';
import {
  ChainResponseData,
  ChainSessionState,
  ChildStoreItem,
  fetchChainChildrenWithRetry,
  getValidatedChainSessionState,
  isChildStoreActive,
  setChildImpersonationSession,
} from 'utils/ChainSessionUtils';
import { getLinkCDNImage } from 'utils/ImageUtils';
import NotifyUtils from 'utils/NotifyUtils';
import styles from './styles.module.css';

const StoreAvatar: FC<{ name?: string; avatarURL?: string; fallbackLetter?: string }> = ({
  name,
  avatarURL,
  fallbackLetter,
}) => {
  const [hasError, setHasError] = useState(false);
  const cdnUrl = avatarURL && !hasError ? getLinkCDNImage(avatarURL) : '';

  if (cdnUrl) {
    return (
      <img
        src={cdnUrl}
        alt={name || 'Avatar'}
        className={styles.avatarImg}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div className={styles.avatarFallback}>
      {fallbackLetter || (name ? name.charAt(0).toUpperCase() : 'S')}
    </div>
  );
};

interface ChainStoreSwitcherProps {
  user: any;
  onClose?: () => void;
}

export const ChainStoreSwitcher: FC<ChainStoreSwitcherProps> = ({ user, onClose }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [switchingAccountId, setSwitchingAccountId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [chainData, setChainData] = useState<ChainResponseData | null>(null);

  const [chainSession, setChainSession] = useState<ChainSessionState | null>(null);

  useEffect(() => {
    setChainSession(getValidatedChainSessionState(user));
  }, [user]);

  const isImpersonating = Boolean(chainSession?.isImpersonating);

  useEffect(() => {
    if (!user?.isChain || !user?.isParent || isImpersonating) {
      return;
    }
    let isMounted = true;

    const loadChildren = async () => {
      setLoading(true);
      const loadedData = await fetchChainChildrenWithRetry(() => !isMounted);
      if (!isMounted) return;
      setLoading(false);

      if (loadedData) {
        setChainData(loadedData);
      } else {
        setChainData(null);
        NotifyUtils.error('Không thể tải danh sách chi nhánh. Vui lòng thử lại sau.');
      }
    };

    loadChildren();

    return () => {
      isMounted = false;
    };
  }, [user?.isChain, user?.isParent, isImpersonating]);

  const filteredChildren = useMemo(() => {
    const list = chainData?.children || [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.code && c.code.toLowerCase().includes(q))
    );
  }, [chainData, searchQuery]);

  const handleSwitchToChild = async (child: ChildStoreItem) => {
    if (switchingAccountId !== null) {
      return;
    }
    setSwitchingAccountId(child.accountID);
    const activeSessionToken = getSessionTokenClient();

    try {
      const resp = await switchChildAccount({
        childAccountId: child.accountID,
      });
      const impersonateToken = resp?.data?.[0]?.accessToken || resp?.data?.[0]?.access_token || resp?.data?.[0]?.token;
      if (isValid(resp) && impersonateToken) {
        const effectiveParentAccountID =
          chainData?.parentAccountID || (user?.accountID ?? user?.account?.accountId ?? user?.accountId ?? 0);
        const effectiveParentName =
          chainData?.parentName || (user?.name ?? user?.businessName ?? user?.account?.fullname ?? '');
        const effectiveParentCode = user?.code || '';
        const effectiveChainCode = chainData?.chainCode || user?.chainCode || '';

        setChildImpersonationSession(
          impersonateToken,
          activeSessionToken,
          {
            accountID: effectiveParentAccountID,
            name: effectiveParentName,
            code: effectiveParentCode,
            chainCode: effectiveChainCode,
          },
          {
            accountID: child.accountID,
            code: child.code,
            name: child.name,
            avatarURL: child.avatarURL,
            phone: child.phone,
            address: child.address,
            chainCode: effectiveChainCode,
          }
        );
        NotifyUtils.success(`Đã chuyển sang chi nhánh: ${child.name}`);
        if (onClose) onClose();
        window.location.reload();
      } else {
        NotifyUtils.error(resp?.message || 'Không thể chuyển sang tài khoản chi nhánh này.');
        setSwitchingAccountId(null);
      }
    } catch (e: any) {
      NotifyUtils.error(e?.message || 'Đã có lỗi xảy ra khi chuyển tài khoản.');
      setSwitchingAccountId(null);
    }
  };

  if (!user?.isChain || !user?.isParent || isImpersonating) {
    return null;
  }

  const parentName = chainData?.parentName || user?.name;
  const parentAvatarURL = chainData?.parentAvatarURL || user?.avatarURL;
  const totalStores = chainData?.totalStores || (chainData?.children ? chainData.children.length + 1 : 1);

  const renderTooltipContent = (name: string, phone?: string, address?: string, isActive = true) => (
    <div className={styles.tooltipContainer}>
      <div className={styles.tooltipName}>{name}</div>
      {phone ? <div className={styles.tooltipMeta}>SĐT: {phone}</div> : null}
      {address ? <div className={styles.tooltipMeta}>Địa chỉ: {address}</div> : null}
      {!isActive && (
        <div className={styles.tooltipInactiveWarning}>
          ⚠️ Tài khoản chi nhánh hiện chưa kích hoạt hoặc tạm khóa. Vui lòng liên hệ CSKH để được hỗ trợ.
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.chainSwitcherContainer}>
      <div className={styles.headerToggle} onClick={() => setExpanded((v) => !v)}>
        <div className={styles.chainTitleGroup}>
          <div className={styles.chainIcon}>
            <StoreIcon fontSize="small" />
          </div>
          <span className={styles.chainName}>Hệ Thống Chi Nhánh</span>
          <span className={styles.badgeCount}>{totalStores} chi nhánh</span>
        </div>
        <ExpandMoreIcon
          fontSize="small"
          className={clsx(styles.chevronIcon, expanded && styles.chevronExpanded)}
        />
      </div>

      {expanded && (
        <div className={styles.dropdownBody}>
          <div className={styles.searchBox}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Tìm kiếm chi nhánh..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Tooltip
            title={renderTooltipContent(parentName, user?.phone, user?.address)}
            arrow
            placement="left"
          >
            <div
              className={clsx(
                styles.parentCard,
                styles.parentCardActive,
                switchingAccountId !== null && styles.parentCardDisabled
              )}
            >
              <div className={styles.storeMeta}>
                <div className={styles.avatarWrapper}>
                  <StoreAvatar name={parentName} avatarURL={parentAvatarURL} fallbackLetter="M" />
                </div>
                <div className={styles.storeText}>
                  <span className={styles.storeName}>{parentName}</span>
                  <span className={styles.parentBadge}>CỬA HÀNG CHÍNH</span>
                </div>
              </div>
              <CheckCircleIcon className={styles.activeCheckIcon} />
            </div>
          </Tooltip>

          <div className={styles.sectionDivider}>
            <span className={styles.sectionDividerText}>Chi nhánh trực thuộc</span>
            <div className={styles.dividerLine} />
          </div>

          {loading ? (
            <div className={styles.loadingNotice}>
              <CircularProgress size={16} className={styles.switchingSpinner} />
              <span>Đang tải danh sách chi nhánh...</span>
            </div>
          ) : (
            <div className={styles.childList}>
              {filteredChildren.length === 0 ? (
                <div className={styles.emptyNotice}>Không tìm thấy chi nhánh phù hợp</div>
              ) : (
                filteredChildren.map((child) => {
                  const isStoreActive = isChildStoreActive(child);
                  const isCurrentActive = false;
                  const isSwitchingThis = switchingAccountId === child.accountID;

                  return (
                    <Tooltip
                      key={child.customerID}
                      title={renderTooltipContent(child.name, child.phone, child.address, isStoreActive)}
                      arrow
                      placement="left"
                    >
                      <div
                        className={clsx(
                          styles.childItem,
                          isCurrentActive && styles.childItemActive,
                          (!isStoreActive || (switchingAccountId !== null && !isSwitchingThis)) && styles.childItemDisabled,
                          switchingAccountId !== null && !isSwitchingThis && styles.itemDisabledDuringSwitch
                        )}
                        onClick={() => {
                          if (!isStoreActive || switchingAccountId !== null) return;
                          if (!isCurrentActive) {
                            handleSwitchToChild(child);
                          }
                        }}
                      >
                        <div className={styles.storeMeta}>
                          <div className={styles.avatarWrapper}>
                            <StoreAvatar name={child.name} avatarURL={child.avatarURL} fallbackLetter="C" />
                          </div>
                          <div className={styles.storeText}>
                            <span className={styles.storeName}>{child.name}</span>
                            {!isStoreActive && (
                              <span className={styles.inactiveStoreBadge}>Chưa kích hoạt</span>
                            )}
                          </div>
                        </div>
                        <div>
                          {!isStoreActive ? (
                            <Tooltip title="Tài khoản chưa kích hoạt - Liên hệ CSKH" arrow placement="top">
                              <div className={styles.disabledLockBtn}>
                                <LockIcon fontSize="small" />
                              </div>
                            </Tooltip>
                          ) : isSwitchingThis ? (
                            <CircularProgress size={16} className={styles.switchingSpinner} />
                          ) : isCurrentActive ? (
                            <CheckCircleIcon className={styles.activeCheckIcon} />
                          ) : (
                            <Tooltip title="Chuyển sang chi nhánh này" arrow placement="top">
                              <div className={styles.switchIconBtn}>
                                <SwapHorizIcon fontSize="small" />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </Tooltip>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChainStoreSwitcher;
